import { useState } from 'react'
import { INCOMING_INVOICE_KINDS, type IncomingInvoiceKind } from '@symphenia/shared-types'
import { Vorschlaege, useSuche } from '@/components/zeilenblock'
import { apiErrorText } from '@/lib/api-error'
import { inputBase, selectBase } from '@/lib/form-styles'
import { createIncomingInvoice, lookupSuppliers } from '../api/incoming-invoices.api'
import { ART_TEXT } from '../lib/texte'
import type { IncomingInvoiceDetail, LookupSupplier } from '../types/incoming-invoices.types'

/**
 * AP-34 Nachtrag 1, 7. — **Anlegen**: nur, was dazu nötig ist — Lieferant und Art. Danach öffnet
 * sich die Rechnung als Entwurf mit Eingangsnummer, und alles Weitere speichert feldweise.
 */
export function CreateInvoiceDialog({ onClose, onAngelegt }: { onClose: () => void; onAngelegt: (r: IncomingInvoiceDetail) => void }) {
  const [text, setText] = useState('')
  const [lieferant, setLieferant] = useState<LookupSupplier | null>(null)
  const [art, setArt] = useState<IncomingInvoiceKind>('INVOICE')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [treffer, setTreffer] = useSuche(text, !lieferant, lookupSuppliers)

  const anlegen = async () => {
    if (!lieferant) return
    setLaeuft(true)
    setFehler(null)
    try {
      onAngelegt(await createIncomingInvoice(lieferant.id, art))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Rechnung ließ sich nicht anlegen.'))
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Eingangsrechnung anlegen"
        className="w-full max-w-lg rounded-lg border border-border p-5 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      >
        <h2 className="text-lg font-semibold text-foreground">Eingangsrechnung anlegen</h2>
        <p className="mt-1 text-sm text-muted-foreground">Lieferant und Art genügen — alles Weitere erfasst du in der Rechnung.</p>

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Lieferant</span>
            <span className="relative block">
              <input
                autoFocus
                className={inputBase}
                placeholder="Name, Lieferantennummer, UID oder IBAN …"
                value={lieferant ? lieferant.name : text}
                onChange={(e) => {
                  setLieferant(null)
                  setText(e.target.value)
                }}
              />
              <Vorschlaege
                eintraege={treffer}
                schluessel={(l) => l.id}
                onWahl={(l) => {
                  setLieferant(l)
                  setTreffer([])
                }}
                darstellung={(l) => (
                  <span>
                    {l.name}
                    <span className="ml-2 text-xs text-muted-foreground">{[l.supplierNumber, l.vatId].filter(Boolean).join(' · ')}</span>
                  </span>
                )}
              />
            </span>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Art</span>
            <select className={selectBase} value={art} onChange={(e) => setArt(e.target.value as IncomingInvoiceKind)}>
              {INCOMING_INVOICE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {ART_TEXT[k]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {fehler && <div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            disabled={!lieferant || laeuft}
            onClick={() => void anlegen()}
          >
            Anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
